import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, CATEGORIES, CATEGORY_NAME, fmtDuration, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, Check, Users, Layers, Film, X, Search, Upload } from "lucide-react";
import ClipUploader from "@/components/ClipUploader";
import { ClipCardWithActions } from "@/components/ClipCard";

export default function GalleryDetail() {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("clips");
  const [showUploader, setShowUploader] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [g, t, u] = await Promise.all([
        api.get(`/galleries/${galleryId}`),
        api.get("/client-reel-templates"),
        api.get("/auth/users"),
      ]);
      setGallery(g.data.gallery);
      setTemplates(t.data.templates || []);
      setUsers(u.data.users || []);
    } catch (e) {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [galleryId]);

  const patch = async (body) => {
    setSaving(true);
    try {
      const r = await api.patch(`/galleries/${galleryId}`, body);
      setGallery((g) => ({ ...g, ...r.data.gallery }));
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveClips = async (clip_ids) => {
    try {
      await api.put(`/galleries/${galleryId}/clips`, { clip_ids });
      setGallery((g) => ({ ...g, clip_ids, clips: (g.clips || []).filter((c) => clip_ids.includes(c.id)) }));
      toast.success(`${clip_ids.length} clip${clip_ids.length === 1 ? "" : "s"} in gallery`);
    } catch (e) {
      toast.error("Failed to update clips");
    }
  };

  const saveClients = async (user_ids) => {
    try {
      await api.put(`/galleries/${galleryId}/clients`, { user_ids });
      setGallery((g) => ({ ...g, client_user_ids: user_ids }));
      toast.success(`${user_ids.length} client${user_ids.length === 1 ? "" : "s"} assigned`);
    } catch (e) {
      toast.error("Failed to assign clients");
    }
  };

  if (loading) return <div className="p-12 text-stone-500">Loading…</div>;
  if (!gallery) return <div className="p-12 text-stone-500">Gallery not found.</div>;

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1400px]" data-testid="gallery-detail-page">
      <div className="flex justify-between items-end mb-1 gap-4">
        <Link to="/galleries" className="text-stone-500 hover:text-stone-200 text-xs uppercase tracking-widest inline-flex items-center gap-1 mb-4" data-testid="gallery-back">
          <ArrowLeft size={12} /> All Galleries
        </Link>
        <button
          onClick={() => setShowUploader(true)}
          className="btn-primary inline-flex items-center gap-2 mb-4"
          data-testid="gallery-upload-clips-btn"
        >
          <Upload size={13} /> Upload Clips
        </button>
      </div>
      <GalleryHeader gallery={gallery} onSave={patch} saving={saving} />

      <div className="flex gap-1 border-b border-stone-800 mt-8 mb-6">
        {[
          { id: "clips", icon: Film, label: `Clips (${(gallery.clip_ids || []).length})` },
          { id: "clients", icon: Users, label: `Clients (${(gallery.client_user_ids || []).length})` },
          { id: "template", icon: Layers, label: "Reel Template" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${tab === t.id ? "border-orange-600 text-stone-100" : "border-transparent text-stone-500 hover:text-stone-200"}`}
            data-testid={`gallery-tab-${t.id}`}
          >
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "clips" && <ClipPicker gallery={gallery} selectedIds={gallery.clip_ids || []} onSave={saveClips} />}
      {tab === "clients" && <ClientPicker users={users} selectedIds={gallery.client_user_ids || []} onSave={saveClients} />}
      {tab === "template" && (
        <TemplatePicker
          galleryId={galleryId}
          templates={templates}
          selectedId={gallery.reel_template_id || ""}
          onSave={(id) => patch({ reel_template_id: id })}
        />
      )}

      {showUploader && (
        <ClipUploader
          galleryId={galleryId}
          galleryName={gallery.name}
          onClose={() => setShowUploader(false)}
          onUploaded={() => { setShowUploader(false); load(); }}
        />
      )}
    </div>
  );
}

function GalleryHeader({ gallery, onSave, saving }) {
  const [name, setName] = useState(gallery.name);
  const [description, setDescription] = useState(gallery.description || "");
  const dirty = name !== gallery.name || description !== (gallery.description || "");

  return (
    <div className="border-l-4 border-orange-600 pl-5">
      <div className="label-tiny mb-2">Gallery</div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bg-transparent font-display uppercase text-4xl font-bold leading-[0.95] w-full outline-none border-b border-transparent focus:border-stone-700"
        data-testid="gallery-name-input"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        placeholder="Short note for your client…"
        className="bg-transparent text-stone-400 mt-3 w-full outline-none resize-none border-b border-transparent focus:border-stone-700"
        data-testid="gallery-description-input"
      />
      {dirty && (
        <button
          onClick={() => onSave({ name, description })}
          disabled={saving}
          className="btn-primary mt-3 inline-flex items-center gap-2"
          data-testid="gallery-save-header"
        >
          <Save size={12} /> {saving ? "Saving…" : "Save changes"}
        </button>
      )}
    </div>
  );
}

function ClipPicker({ gallery, selectedIds, onSave }) {
  const isSocial = gallery?.kind === "social_media";
  const [events, setEvents] = useState([]);
  const [clips, setClips] = useState([]);
  const [eventName, setEventName] = useState("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(new Set(selectedIds));
  const [loadingClips, setLoadingClips] = useState(false);
  // Default view: only the clips actually IN this gallery. Flip to "browse
  // the whole library" mode when the studio owner wants to add more.
  const [onlyInGallery, setOnlyInGallery] = useState(true);

  useEffect(() => { setPicked(new Set(selectedIds)); }, [selectedIds.join("|")]);

  useEffect(() => {
    if (isSocial) return; // social galleries don't need the library-wide event list
    api.get("/events").then((r) => setEvents(r.data.events || []));
  }, [isSocial]);

  useEffect(() => {
    const params = {};
    if (isSocial || onlyInGallery) {
      // Scope the fetch to just this gallery's clips.
      params.gallery_id = gallery.id;
    } else {
      if (eventName) params.event_name = eventName;
      if (category) params.category = category;
    }
    if (search) params.search = search;
    setLoadingClips(true);
    api.get("/clips", { params })
      .then((r) => setClips(r.data || []))
      .finally(() => setLoadingClips(false));
  }, [isSocial, onlyInGallery, gallery?.id, eventName, category, search, selectedIds.join("|")]);

  const dirty = useMemo(() => {
    const a = new Set(selectedIds);
    if (a.size !== picked.size) return true;
    for (const x of a) if (!picked.has(x)) return true;
    return false;
  }, [selectedIds, picked]);

  const toggle = (id) => {
    setPicked((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const addAllVisible = () => {
    setPicked((p) => {
      const n = new Set(p);
      clips.forEach((c) => n.add(c.id));
      return n;
    });
  };

  const clearAllVisible = () => {
    setPicked((p) => {
      const n = new Set(p);
      clips.forEach((c) => n.delete(c.id));
      return n;
    });
  };

  return (
    <div data-testid="gallery-clip-picker">
      <div className="bg-stone-900/40 border border-stone-800 p-4 mb-4 flex flex-wrap items-end gap-3">
        {!isSocial && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setOnlyInGallery(true)}
              className={`btn-secondary !py-2 text-xs whitespace-nowrap ${onlyInGallery ? "!text-orange-500 !border-orange-700" : ""}`}
              data-testid="picker-scope-in"
            >
              In this gallery ({(selectedIds || []).length})
            </button>
            <button
              type="button"
              onClick={() => setOnlyInGallery(false)}
              className={`btn-secondary !py-2 text-xs whitespace-nowrap ${!onlyInGallery ? "!text-orange-500 !border-orange-700" : ""}`}
              data-testid="picker-scope-all"
            >
              Add from library
            </button>
          </div>
        )}
        {!isSocial && !onlyInGallery && (
          <>
            <div className="min-w-[200px] flex-1">
              <div className="label-tiny mb-1">Event</div>
              <select value={eventName} onChange={(e) => setEventName(e.target.value)} className="field-input w-full" data-testid="picker-event">
                <option value="">All events</option>
                {events.map((e) => (
                  <option key={e.name} value={e.name}>
                    {e.name} {e.date ? `· ${e.date}` : ""} · {e.clip_count} clip{e.clip_count !== 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[160px]">
              <div className="label-tiny mb-1">Category</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="field-input w-full" data-testid="picker-category">
                <option value="">All</option>
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </>
        )}
        <div className="min-w-[200px] flex-1">
          <div className="label-tiny mb-1">Search</div>
          <div className="relative">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={(isSocial || onlyInGallery) ? "Search this gallery…" : "Rider name, label, notes…"}
              className="field-input w-full pl-8"
              data-testid="picker-search"
            />
          </div>
        </div>
        {!isSocial && !onlyInGallery && (
          <div className="flex gap-2">
            <button onClick={addAllVisible} className="btn-secondary !py-2 text-xs whitespace-nowrap" data-testid="picker-add-all">
              <Plus size={11} className="inline mr-1" /> Add all visible
            </button>
            <button onClick={clearAllVisible} className="btn-secondary !py-2 text-xs whitespace-nowrap" data-testid="picker-clear-all">
              Clear visible
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="text-stone-400 text-sm">
          <span className="text-orange-400 font-mono-tc text-lg">{picked.size}</span> clip{picked.size === 1 ? "" : "s"} in this gallery
          {dirty && <span className="ml-3 text-amber-500">· unsaved changes</span>}
        </div>
        <button
          onClick={() => onSave(Array.from(picked))}
          disabled={!dirty}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="picker-save"
        >
          <Save size={12} /> Save clips
        </button>
      </div>

      {loadingClips ? (
        <div className="text-stone-500 py-8 text-center">Loading clips…</div>
      ) : clips.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-12 text-center text-stone-500" data-testid="picker-empty">
          {(isSocial || onlyInGallery)
            ? "No clips in this gallery yet. Click Upload Clips (top right) or switch to Add from library."
            : "No clips match those filters."}
        </div>
      ) : (isSocial || onlyInGallery) ? (
        // Gallery-mode: render the full Library ClipCard for each clip so the
        // studio owner has every action (play, edit, share, download, assign,
        // Vimeo, delete, thumbs up/down, etc.) available on the gallery page too.
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="picker-grid-gallery">
          {clips.map((c) => (
            <ClipCardWithActions
              key={c.id}
              clip={c}
              onChanged={() => {
                // Reload the gallery-scoped clip list after any change.
                setSearch((s) => s);
                // Trigger the fetch effect by touching a dependency:
                setLoadingClips(true);
                api.get("/clips", { params: { gallery_id: gallery.id, ...(search ? { search } : {}) } })
                  .then((r) => setClips(r.data || []))
                  .finally(() => setLoadingClips(false));
              }}
              onDelete={async () => {
                // Removing from gallery is safer than deleting the underlying
                // clip — the studio owner probably didn't mean "wipe forever".
                if (!window.confirm(`Remove "${c.rider_horse_name || c.sub_label || "this clip"}" from this gallery?`)) return;
                try {
                  const nextIds = (gallery.clip_ids || []).filter((id) => id !== c.id);
                  await api.put(`/galleries/${gallery.id}/clips`, { clip_ids: nextIds });
                  toast.success("Removed from gallery");
                  onSave(nextIds);
                } catch (err) {
                  toast.error("Remove failed: " + (err?.response?.data?.detail || err.message));
                }
              }}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="picker-grid">
          {clips.map((c) => {
            const on = picked.has(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggle(c.id)}
                className={`text-left border transition-colors ${on ? "border-orange-600" : "border-stone-800 hover:border-stone-700"}`}
                data-testid={`pick-clip-${c.id}`}
              >
                <div className="aspect-video bg-black relative overflow-hidden">
                  {c.stream_url ? (
                    <video src={c.stream_url} muted preload="metadata" playsInline className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-700"><Film size={20} /></div>
                  )}
                  <div className="absolute top-1.5 left-1.5">
                    <span className="tag-cat backdrop-blur-sm bg-stone-900/80 text-[10px]">{CATEGORY_NAME[c.category]}</span>
                  </div>
                  {on && (
                    <div className="absolute inset-0 bg-orange-600/20 flex items-center justify-center">
                      <div className="bg-orange-600 text-white rounded-full p-1.5"><Check size={14} /></div>
                    </div>
                  )}
                  <div className="absolute bottom-1.5 right-1.5 font-mono-tc text-[10px] text-orange-300 bg-black/80 px-1.5 py-0.5">
                    {fmtDuration(c.duration)}
                  </div>
                </div>
                <div className="p-2">
                  <div className="text-xs font-medium truncate" title={c.rider_horse_name || c.sub_label}>
                    {c.rider_horse_name || c.sub_label || "Untitled"}
                  </div>
                  {c.sub_label && c.rider_horse_name && (
                    <div className="text-[10px] text-orange-400/80 truncate">{c.sub_label}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ClientPicker({ users, selectedIds, onSave }) {
  const [picked, setPicked] = useState(new Set(selectedIds));
  const [search, setSearch] = useState("");
  useEffect(() => { setPicked(new Set(selectedIds)); }, [selectedIds.join("|")]);

  const filtered = users.filter((u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
  });

  const toggle = (id) => {
    setPicked((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const dirty = useMemo(() => {
    const a = new Set(selectedIds);
    if (a.size !== picked.size) return true;
    for (const x of a) if (!picked.has(x)) return true;
    return false;
  }, [selectedIds, picked]);

  return (
    <div data-testid="gallery-client-picker">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter users by name or email…"
          className="field-input flex-1 min-w-[200px] max-w-md"
          data-testid="client-picker-search"
        />
        <div className="text-sm text-stone-400">
          <span className="text-orange-400 font-mono-tc text-lg">{picked.size}</span> selected
          {dirty && <span className="ml-3 text-amber-500">· unsaved</span>}
        </div>
        <button onClick={() => onSave(Array.from(picked))} disabled={!dirty} className="btn-primary inline-flex items-center gap-2" data-testid="client-picker-save">
          <Save size={12} /> Save clients
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-12 text-center text-stone-500">
          <p>No client users yet.</p>
          <Link to="/users" className="text-orange-500 hover:text-orange-400 text-sm mt-2 inline-block">Create a user →</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {filtered.map((u) => {
            const on = picked.has(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggle(u.id)}
                className={`text-left p-3 border transition-colors flex items-center justify-between ${on ? "border-orange-600 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`}
                data-testid={`pick-user-${u.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                  <div className="text-xs text-stone-500 truncate">{u.email}</div>
                  <div className="text-[10px] uppercase tracking-widest text-orange-500/80 mt-0.5">{u.role}</div>
                </div>
                {on && <div className="bg-orange-600 text-white rounded-full p-1 ml-2 shrink-0"><Check size={12} /></div>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TemplatePicker({ galleryId, templates, selectedId, onSave }) {
  // Only show templates that have this gallery in their gallery_ids
  const eligible = (templates || []).filter((t) => (t.gallery_ids || []).includes(galleryId));

  return (
    <div data-testid="gallery-template-picker">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-stone-400 text-sm max-w-xl">
          Pick which template auto-selects when a client builds their reel. Only templates assigned to this gallery appear here — manage assignments on the <Link to="/client-templates" className="text-orange-400 hover:text-orange-300">Client Templates</Link> page.
        </p>
        <Link to="/client-templates" className="btn-secondary inline-flex items-center gap-2 text-xs" data-testid="manage-templates-link">
          <Layers size={12} /> Manage Templates
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          onClick={() => onSave("")}
          className={`text-left p-4 border transition-colors ${!selectedId ? "border-orange-600 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`}
          data-testid="template-pick-none"
        >
          <div className="font-display uppercase font-semibold mb-1">No Default Template</div>
          <div className="text-xs text-stone-500">Clients still pick from eligible templates when building their reel.</div>
        </button>
        {eligible.map((t) => {
          const on = t.id === selectedId;
          return (
            <button key={t.id} onClick={() => onSave(t.id)} className={`text-left p-4 border transition-colors ${on ? "border-orange-600 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`} data-testid={`template-pick-${t.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-display uppercase font-semibold">{t.name}</div>
                {on && <div className="bg-orange-600 text-white rounded-full p-1"><Check size={10} /></div>}
              </div>
              <div className="text-xs text-stone-500 mb-2 line-clamp-2">{t.description}</div>
              <div className="flex gap-3 text-[10px] uppercase tracking-widest text-stone-500 flex-wrap">
                <span>{t.transition_style}</span>
                {t.music_url && <span>• music</span>}
                {t.sponsor_text && <span>• sponsor</span>}
                {t.intro_card_text && <span>• intro card</span>}
              </div>
            </button>
          );
        })}
      </div>

      {eligible.length === 0 && (
        <div className="border border-dashed border-stone-800 p-10 text-center mt-3" data-testid="no-eligible-templates">
          <Layers size={28} className="mx-auto text-stone-600 mb-2" />
          <p className="text-stone-400 text-sm">No templates assigned to this gallery yet.</p>
          <Link to="/client-templates" className="text-orange-400 hover:text-orange-300 text-sm mt-2 inline-block">Open Client Templates →</Link>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="label-tiny mb-1">{label}{required && <span className="text-orange-500"> *</span>}</div>
      {children}
    </div>
  );
}
